import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, Stat, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import { useLoadBalancerStats, type LbStatValue, type LbStatGroup } from '@/api/opennebula';
import { formatBytes } from '@/pages/services/_shared/analytics-format';

function statusTone(s?: string): StatusTone {
  const v = (s ?? '').toUpperCase();
  if (v === 'UP' || v === 'OPEN') return 'success';
  if (v === 'DOWN') return 'danger';
  if (v === 'NOLB' || v === 'MAINT') return 'warning';
  return 'muted';
}

export function LbStats({ serviceId, lbId }: { serviceId: string; lbId: string }) {
  const { data: groups = [], isLoading } = useLoadBalancerStats(serviceId, lbId);

  const summary = useMemo(() => {
    let sessions = 0, current = 0, bin = 0, bout = 0;
    for (const g of groups) {
      const fe = g.values.find((v) => v.title === 'Frontend');
      if (fe) { sessions += fe.session_total ?? 0; current += fe.session_current ?? 0; bin += fe.byte_in ?? 0; bout += fe.byte_out ?? 0; }
    }
    return { sessions, current, bin, bout };
  }, [groups]);

  if (isLoading) return null;
  if (groups.length === 0) {
    return <Section title="Statistics" description="Live traffic metrics."><EmptyState icon={<Activity size={28} />} title="No statistics" description="Stats appear once the load balancer is serving traffic." /></Section>;
  }

  return (
    <Section title="Statistics" description="Live traffic metrics (auto-refreshing).">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-4)' }}>
        <Card flat><CardBody><Stat label="Total sessions" value={summary.sessions.toLocaleString()} /></CardBody></Card>
        <Card flat><CardBody><Stat label="Current sessions" value={summary.current.toLocaleString()} /></CardBody></Card>
        <Card flat><CardBody><Stat label="Bytes in" value={formatBytes(summary.bin)} /></CardBody></Card>
        <Card flat><CardBody><Stat label="Bytes out" value={formatBytes(summary.bout)} /></CardBody></Card>
      </div>

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {groups.map((g) => <ListenerStat key={g.id} group={g} />)}
      </div>
    </Section>
  );
}

const serverColumns: ColumnDef<LbStatValue, unknown>[] = [
  { id: 'server', header: 'Server', cell: ({ row }) => (row.original.server_name || row.original.name || '').replace(/^lb\d+_/, '') },
  { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={statusTone(row.original.status)}>{row.original.status || '—'}</StatusPill> },
  { id: 'sessions', header: 'Sessions', cell: ({ row }) => (row.original.session_current ?? 0).toLocaleString() },
  { id: 'max', header: 'Max', cell: ({ row }) => (row.original.session_max ?? 0).toLocaleString() },
  { id: 'in', header: 'In', cell: ({ row }) => formatBytes(row.original.byte_in) },
  { id: 'out', header: 'Out', cell: ({ row }) => formatBytes(row.original.byte_out) },
  { id: 'http2xx', header: '2xx', cell: ({ row }) => (row.original.http_success ?? 0).toLocaleString() },
  { id: 'http4xx', header: '4xx', cell: ({ row }) => (row.original.http_client ?? 0).toLocaleString() },
  { id: 'http5xx', header: '5xx', cell: ({ row }) => (row.original.http_error ?? 0).toLocaleString() },
];

function ListenerStat({ group }: { group: LbStatGroup }) {
  const fe = group.values.find((v) => v.title === 'Frontend');
  const servers = group.values.filter((v) => v.title === 'Server');
  return (
    <Card>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--vn-space-3)' }}>
          <strong>{fe?.name || group.proxy_name}</strong>
          {fe?.status && <StatusPill tone={statusTone(fe.status)}>{fe.status}</StatusPill>}
        </div>
        {servers.length === 0 ? (
          <EmptyState icon={<Activity size={20} />} title="No backend servers" description="Backend servers appear once the listener starts reporting traffic." />
        ) : (
          <DataTable columns={serverColumns} data={servers} getRowId={(_s, i) => String(i)} />
        )}
      </CardBody>
    </Card>
  );
}
